import React, { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { MoveToVaultButton } from "./MoveToVault";
import { useAuth } from "../context/AuthContext";

// The Legacy Suite = Lexarra estate-planning experience, embedded via iframe with a
// server-to-server auth handshake. Family Vault owns the wallet; Lexarra meters credits.
export function LegacyEstate() {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [credits, setCredits] = useState(null);
  const [docs, setDocs] = useState([]);
  const [showDocs, setShowDocs] = useState(false);
  const lexOriginRef = useRef(null);

  const loadDocs = useCallback(async () => {
    try {
      const { data } = await api.get("/embed/lexarra/documents");
      setDocs(data || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const loadEmbed = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/embed/lexarra/token");
      if (!data.ok) {
        setError(data.error || "The Legacy Suite is unavailable right now.");
        setCredits(data.credits_balance ?? null);
        return;
      }
      setEmbedUrl(data.embed_url);
      setCredits(data.credits_balance);
      try { lexOriginRef.current = new URL(data.embed_url).origin; } catch (e) { /* ignore */ }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "The Legacy Suite is unavailable right now.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEmbed(); loadDocs(); }, [loadEmbed, loadDocs]);

  // Listen for messages emitted from inside the Lexarra iframe.
  useEffect(() => {
    const onMessage = async (ev) => {
      if (lexOriginRef.current && ev.origin !== lexOriginRef.current) return;
      const msg = ev.data || {};
      if (msg.type === "lexarra.move_to_vault" && msg.document_id) {
        try {
          const { data } = await api.post("/embed/lexarra/import-document", {
            document_id: msg.document_id, title: msg.title, doc_type: msg.doc_type,
          });
          if (data.ok) { toast.success("Saved to your Secret Vault"); loadDocs(); }
          else { toast.error(data.error || "Could not import that document."); }
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Could not import that document.");
        }
      } else if (msg.type === "lexarra.out_of_credits" || msg.type === "lexarra.buy_credits") {
        window.dispatchEvent(new Event("fv:open-credits"));
      } else if (msg.type === "lexarra.credits_changed") {
        refreshUser && refreshUser();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [loadDocs, refreshUser]);

  return (
    <div className="flex flex-col flex-1 min-h-0 lg:-mx-5 lg:-mb-3" data-testid="legacy-estate">
      {/* Full-bleed embed so it reads as a native vault page, not a boxed iframe */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {loading && (
          <div className="absolute inset-0 grid place-items-center" style={{ background: "var(--bg-base)" }}>
            <Loader2 className="animate-spin gold-text" />
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 grid place-items-center text-center px-6" data-testid="legacy-error">
            <div>
              <FileText size={30} className="gold-text mx-auto mb-3" />
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{error}</p>
              <button onClick={loadEmbed} data-testid="legacy-retry"
                className="mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold"
                style={{ background: "var(--accent)", color: "#fff" }}>
                <RefreshCw size={14} /> Try again
              </button>
            </div>
          </div>
        )}
        {embedUrl && !error && (
          <iframe title="Lexarra Estate Planning" src={embedUrl} data-testid="legacy-iframe"
            className="w-full h-full" style={{ border: "none", background: "#0B0B12", height: "100%" }}
            allow="camera; microphone; clipboard-write" />
        )}

        {/* Floating "saved to Secret Vault" chip + popover */}
        {!error && docs.length > 0 && (
          <div className="absolute top-3 left-5 z-10">
            <button onClick={() => setShowDocs((s) => !s)} data-testid="legacy-vault-docs-toggle"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: "rgba(11,11,18,0.72)", backdropFilter: "blur(8px)", border: "1px solid var(--border)" }}>
              <FileText size={13} className="gold-text" /> {docs.length} in Secret Vault
            </button>
            {showDocs && (
              <div className="mt-1.5 w-64 rounded-xl p-2 space-y-1 max-h-64 overflow-y-auto no-scrollbar"
                style={{ background: "rgba(11,11,18,0.94)", backdropFilter: "blur(8px)", border: "1px solid var(--border)" }}
                data-testid="legacy-vault-docs">
                {docs.map((d) => (
                  <div key={d.id} className="rounded-lg px-2 py-1.5 space-y-1.5"
                    style={{ background: "var(--overlay)" }} data-testid="legacy-vault-doc">
                    <div className="flex items-center gap-2">
                      <FileText size={13} className="gold-text shrink-0" />
                      <span className="text-xs truncate flex-1">{d.title}</span>
                    </div>
                    <MoveToVaultButton testid={`legacy-move-to-vault-${d.id}`} label="Schedule a release"
                      className="w-full flex items-center justify-center gap-1.5 rounded-md py-1.5 text-[11px] font-semibold"
                      media={{ path: d.storage_path || null, kind: "pdf", title: d.title || "Legal document", source: "lexarra", ref_id: d.id, ref_collection: "secret_vault_items" }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
